import * as Sentry from '@sentry/react';
import { Suspense, useMemo } from 'react';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';

import { useAuth } from '@/app/core/auth/application/hooks/useAuth';
import useTranslations from '@/lang/useTranslations';
import { useApp } from '@/platform/app/useApp';
import { landingFor, modulesFor, useViewer } from '@/platform/auth/audience';
import { ROUTES } from '@/platform/router/routes';
import { TenkoAppShell } from '@/shared/components/layout/TenkoAppShell';
import { TenkoAppSidebar } from '@/shared/components/layout/TenkoAppSidebar';
// eslint-disable-next-line no-restricted-imports -- no Tenko* spinner wrapper yet (design-system.md backlog)
import { Spinner } from '@/shared/components/primitives/spinner';

import { ImpersonationBanner } from './ImpersonationBanner';
import { NotificationsBell } from './NotificationsBell';
import { useShellNav } from './useShellNav';

/**
 * The feature routes live here, not in the data router, so this is what gives a
 * transaction a name like `/app/agents/*` instead of the `/app/*` the root
 * router sees. Without it every page in the dashboard shares one name.
 */
const TracedRoutes = Sentry.withSentryReactRouterV7Routing(Routes);

/**
 * Authenticated area (`/app/*`): TenkoAppShell + sidebar built from the app
 * definition, routes mounted from the module manifests (lazy, behind one
 * Suspense boundary), and the app's landing route as catch-all.
 *
 * Everything is narrowed to the viewer's audience: customers get the customer
 * modules, staff get the console. Unmounted routes cannot be reached, so the
 * filter doubles as the route guard.
 */
export const DashboardShell = () => {
  const navigate = useNavigate();
  const app = useApp();
  const { user, doLogout } = useAuth();
  const { translation: t } = useTranslations('global');
  const viewer = useViewer();
  const { sections, footerItems } = useShellNav(app, viewer);
  const landing = landingFor(app, viewer);

  const routes = useMemo(
    () => modulesFor(app, viewer).flatMap((m) => m.routes ?? []),
    [app, viewer],
  );

  const fullName = [user?.name, user?.last_name].filter(Boolean).join(' ');
  const userPayload = {
    name: fullName || user?.email || 'User',
    email: user?.email ?? '',
    avatar: user?.profile_image,
  };

  const navUserLabels = {
    account: t('sidebar_account_label'),
    settings: t('sidebar_settings_label'),
    logout: t('logout'),
    organization: t('sidebar_organization_label'),
  };

  const { Wordmark } = app.brand;
  const brand = {
    label: Wordmark ? <Wordmark className="text-base" /> : app.brand.name,
    // A brand's home link is a customer route; staff go back to their console.
    to: viewer === 'internal' ? landing : (app.brand.homeTo ?? landing),
    icon: <app.brand.Logo iconOnly className="text-white" />,
  };

  return (
    <TenkoAppShell
      sidebar={
        <TenkoAppSidebar
          sections={sections}
          footerItems={footerItems}
          brand={brand}
          user={userPayload}
          navUserLabels={navUserLabels}
          onAccount={() => navigate(ROUTES.account)}
          onSettings={() => navigate(ROUTES.settings)}
          onLogout={() => {
            doLogout();
            navigate(ROUTES.auth);
          }}
        />
      }
      headerActions={<NotificationsBell />}
    >
      <ImpersonationBanner />
      <Suspense
        fallback={
          <div className="w-full h-full flex justify-center items-center">
            <Spinner className="size-8 text-primary" aria-label="Loading page" />
          </div>
        }
      >
        <TracedRoutes>
          {routes.map(({ path, to, Component }) => (
            <Route path={path} key={to} element={<Component />} />
          ))}
          <Route
            path="/*"
            element={<Navigate to={landing} replace />}
          />
        </TracedRoutes>
      </Suspense>
    </TenkoAppShell>
  );
};
